import { useRef, useEffect } from "react";
import { CONTENT_H, CONTENT_W, fillRoundRect, fillTriangle, randInt } from "./chunk-0.jsx";
import { roboMoodFromEmotion, shouldRoboConfusedAnim, shouldRoboLaughAnim, shouldRoboWink } from "./chunk-1.jsx";
export function RoboEyes({
  emotion,
  poke,
  heartbeatRef,
  accent,
  active = true,
  motionBoost = false
}) {
  const canvasRef = useRef(null);
  const stateRef = useRef({
    // geometry (aligned with RoboEyesDisplay config)
    screenWidth: CONTENT_W,
    screenHeight: CONTENT_H,
    eyeWDefault: 110,
    eyeHDefault: 110,
    eyeWCurrent: 110,
    // RoboEyes.begin(): start with closed eyes (heightCurrent=1), then open() is called by firmware
    eyeHCurrentL: 1,
    eyeHCurrentR: 1,
    eyeWNext: 110,
    eyeHNextL: 110,
    eyeHNextR: 110,
    eyeHeightOffsetL: 0,
    eyeHeightOffsetR: 0,
    borderRDefault: 24,
    borderRCurrentL: 24,
    borderRCurrentR: 24,
    borderRNext: 24,
    spaceDefault: 18,
    spaceCurrent: 18,
    spaceNext: 18,
    // coords
    eyeLx: 0,
    eyeLy: 0,
    eyeLxNext: 0,
    eyeLyNext: 0,
    eyeRx: 0,
    eyeRy: 0,
    eyeRxNext: 0,
    eyeRyNext: 0,
    // eyelids
    eyelidsTiredHeight: 0,
    eyelidsTiredHeightNext: 0,
    eyelidsAngryHeight: 0,
    eyelidsAngryHeightNext: 0,
    eyelidsHappyBottomOffset: 0,
    eyelidsHappyBottomOffsetNext: 0,
    // macro anims
    curious: true,
    cyclops: false,
    hFlicker: false,
    hFlickerAlternate: false,
    hFlickerAmplitude: 2,
    vFlicker: false,
    vFlickerAlternate: false,
    vFlickerAmplitude: 10,
    // blink/idle timers (RoboEyes defaults to 0 => first frame triggers once)
    autoblinker: true,
    blinkIntervalS: 4,
    blinkVariationS: 2,
    blinkTimerMs: 0,
    idle: true,
    idleIntervalS: 3,
    idleVariationS: 2,
    idleTimerMs: 0,
    // open() is called by firmware right after begin()
    eyeLOpen: true,
    eyeROpen: true,
    // one-shot animations
    confused: false,
    confusedToggle: true,
    confusedTimerMs: 0,
    confusedDurationMs: 500,
    laugh: false,
    laughToggle: true,
    laughTimerMs: 0,
    laughDurationMs: 500,
    winkLeftNext: true,
    // loop
    lastEmotion: '',
    lastTickMs: 0,
    lastPaintMs: 0
  });

  // init defaults (centered)
  useEffect(() => {
    const s = stateRef.current;
    const constraintX = () => s.screenWidth - (s.eyeWDefault + s.spaceDefault + s.eyeWDefault);
    const constraintY = () => s.screenHeight - s.eyeHDefault;
    // RoboEyes.begin() default positions
    s.eyeLx = Math.trunc(constraintX() / 2);
    s.eyeLy = Math.trunc(constraintY() / 2);
    s.eyeLxNext = s.eyeLx;
    s.eyeLyNext = s.eyeLy;
    s.eyeRx = s.eyeLx + s.eyeWDefault + s.spaceDefault;
    s.eyeRy = s.eyeLy;
    s.eyeRxNext = s.eyeRx;
    s.eyeRyNext = s.eyeRy;
    // RoboEyes 默认 timer=0：第一次 drawEyes 会触发一次 blink/idle，然后自己推迟到下一次
    s.blinkTimerMs = 0;
    s.idleTimerMs = 0;
  }, []);
  useEffect(() => {
    const s = stateRef.current;
    // 自检：强制触发一次眨眼 + 立即扫视 + 轻微“confused”抖动
    s.blinkTimerMs = 0;
    s.idleTimerMs = 0;
    s.confused = true;
  }, [poke]);
  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.floor(CONTENT_W * dpr);
    canvas.height = Math.floor(CONTENT_H * dpr);
    canvas.style.width = `${CONTENT_W}px`;
    canvas.style.height = `${CONTENT_H}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const BG = '#000000';
    const MAIN = accent || '#ff9600'; // firmware default: (255,150,0)

    const closeBoth = s => {
      s.eyeHNextL = 1;
      s.eyeHNextR = 1;
      s.eyeLOpen = false;
      s.eyeROpen = false;
    };
    const openBoth = s => {
      s.eyeLOpen = true;
      s.eyeROpen = true;
    };
    const blinkBoth = s => {
      closeBoth(s);
      openBoth(s);
    };
    const closeSingle = (s, left, right) => {
      if (left) {
        s.eyeHNextL = 1;
        s.eyeLOpen = false;
      }
      if (right) {
        s.eyeHNextR = 1;
        s.eyeROpen = false;
      }
    };
    const openSingle = (s, left, right) => {
      if (left) s.eyeLOpen = true;
      if (right) s.eyeROpen = true;
    };
    const blinkSingle = (s, left, right) => {
      closeSingle(s, left, right);
      openSingle(s, left, right);
    };
    const getConstraintX = s => s.screenWidth - s.eyeWCurrent - s.spaceCurrent - s.eyeWCurrent;
    const getConstraintY = s => s.screenHeight - s.eyeHDefault;
    const tick = now => {
      const s = stateRef.current;

      // 30fps paint
      if (s.lastPaintMs && now - s.lastPaintMs < 33) return;
      s.lastPaintMs = now;
      if (heartbeatRef) heartbeatRef.current = now;

      // emotion -> mood + one-shot actions (match firmware mapping)
      if (emotion && emotion !== s.lastEmotion) {
        s.lastEmotion = emotion;
        if (shouldRoboConfusedAnim(emotion)) s.confused = true;
        if (shouldRoboLaughAnim(emotion)) s.laugh = true;
        if (shouldRoboWink(emotion)) {
          const left = s.winkLeftNext;
          s.winkLeftNext = !s.winkLeftNext;
          blinkSingle(s, left, !left);
        }
      }

      // Reset to firmware config (device/xiaozhi-esp32/main/display/roboeyes_display.cc)
      s.eyeWNext = s.eyeWDefault;
      s.spaceNext = s.spaceDefault;
      s.borderRNext = s.borderRDefault;
      s.autoblinker = true;
      s.idle = true;
      s.blinkIntervalS = 4;
      s.blinkVariationS = 2;
      s.idleIntervalS = 3;
      s.idleVariationS = 2;

      // size tweens
      if (s.curious) {
        s.eyeHeightOffsetL = s.eyeLxNext <= 10 ? 8 : 0;
        s.eyeHeightOffsetR = s.eyeRxNext >= s.screenWidth - s.eyeWCurrent - 10 ? 8 : 0;
      } else {
        s.eyeHeightOffsetL = 0;
        s.eyeHeightOffsetR = 0;
      }
      s.eyeHCurrentL = Math.floor((s.eyeHCurrentL + s.eyeHNextL + s.eyeHeightOffsetL) / 2);
      s.eyeLy += Math.trunc((s.eyeHDefault - s.eyeHCurrentL) / 2);
      s.eyeLy -= Math.trunc(s.eyeHeightOffsetL / 2);
      s.eyeHCurrentR = Math.floor((s.eyeHCurrentR + s.eyeHNextR + s.eyeHeightOffsetR) / 2);
      s.eyeRy += Math.trunc((s.eyeHDefault - s.eyeHCurrentR) / 2);
      s.eyeRy -= Math.trunc(s.eyeHeightOffsetR / 2);
      if (s.eyeLOpen && s.eyeHCurrentL <= 1 + s.eyeHeightOffsetL) s.eyeHNextL = s.eyeHDefault;
      if (s.eyeROpen && s.eyeHCurrentR <= 1 + s.eyeHeightOffsetR) s.eyeHNextR = s.eyeHDefault;
      s.eyeWCurrent = Math.floor((s.eyeWCurrent + s.eyeWNext) / 2);
      s.spaceCurrent = Math.floor((s.spaceCurrent + s.spaceNext) / 2);
      s.eyeLx = Math.floor((s.eyeLx + s.eyeLxNext) / 2);
      s.eyeLy = Math.floor((s.eyeLy + s.eyeLyNext) / 2);
      s.eyeRxNext = s.eyeLxNext + s.eyeWCurrent + s.spaceCurrent;
      s.eyeRyNext = s.eyeLyNext;
      s.eyeRx = Math.floor((s.eyeRx + s.eyeRxNext) / 2);
      s.eyeRy = Math.floor((s.eyeRy + s.eyeRyNext) / 2);
      s.borderRCurrentL = Math.floor((s.borderRCurrentL + s.borderRNext) / 2);
      s.borderRCurrentR = Math.floor((s.borderRCurrentR + s.borderRNext) / 2);

      // auto blink
      if (s.autoblinker && now >= s.blinkTimerMs) {
        blinkBoth(s);
        s.blinkTimerMs = now + s.blinkIntervalS * 1000 + randInt(s.blinkVariationS * 1000);
      }

      // laughing (vertical flicker for 500ms)
      if (s.laugh) {
        if (s.laughToggle) {
          s.vFlicker = true;
          s.vFlickerAmplitude = 5;
          s.laughTimerMs = now;
          s.laughToggle = false;
        } else if (now >= s.laughTimerMs + s.laughDurationMs) {
          s.vFlicker = false;
          s.laughToggle = true;
          s.laugh = false;
        }
      }

      // confused (horizontal flicker for 500ms)
      if (s.confused) {
        if (s.confusedToggle) {
          s.hFlicker = true;
          s.hFlickerAmplitude = 20;
          s.confusedTimerMs = now;
          s.confusedToggle = false;
        } else if (now >= s.confusedTimerMs + s.confusedDurationMs) {
          s.hFlicker = false;
          s.confusedToggle = true;
          s.confused = false;
        }
      }

      // idle: random positions
      if (s.idle && now >= s.idleTimerMs) {
        // RoboEyes: random(getScreenConstraint_X/Y) => [0..maxExclusive)
        s.eyeLxNext = randInt(Math.max(1, getConstraintX(s)));
        s.eyeLyNext = randInt(Math.max(1, getConstraintY(s)));
        s.idleTimerMs = now + s.idleIntervalS * 1000 + randInt(s.idleVariationS * 1000);
      }

      // apply flickers
      if (s.hFlicker) {
        if (s.hFlickerAlternate) {
          s.eyeLx += s.hFlickerAmplitude;
          s.eyeRx += s.hFlickerAmplitude;
        } else {
          s.eyeLx -= s.hFlickerAmplitude;
          s.eyeRx -= s.hFlickerAmplitude;
        }
        s.hFlickerAlternate = !s.hFlickerAlternate;
      }
      if (s.vFlicker) {
        if (s.vFlickerAlternate) {
          s.eyeLy += s.vFlickerAmplitude;
          s.eyeRy += s.vFlickerAmplitude;
        } else {
          s.eyeLy -= s.vFlickerAmplitude;
          s.eyeRy -= s.vFlickerAmplitude;
        }
        s.vFlickerAlternate = !s.vFlickerAlternate;
      }

      // draw
      ctx.clearRect(0, 0, CONTENT_W, CONTENT_H);

      // eyes
      fillRoundRect(ctx, s.eyeLx, s.eyeLy, s.eyeWCurrent, s.eyeHCurrentL, s.borderRCurrentL, MAIN);
      if (!s.cyclops) {
        fillRoundRect(ctx, s.eyeRx, s.eyeRy, s.eyeWCurrent, s.eyeHCurrentR, s.borderRCurrentR, MAIN);
      }

      // mood overlays (tired/angry/happy)
      const mood = roboMoodFromEmotion(emotion);
      const tired = mood === 'tired';
      const angry = mood === 'angry';
      const happy = mood === 'happy';
      s.eyelidsTiredHeightNext = tired ? Math.floor(s.eyeHCurrentL / 2) : 0;
      s.eyelidsAngryHeightNext = angry ? Math.floor(s.eyeHCurrentL / 2) : 0;
      s.eyelidsHappyBottomOffsetNext = happy ? Math.floor(s.eyeHCurrentL / 2) : 0;
      s.eyelidsTiredHeight = Math.floor((s.eyelidsTiredHeight + s.eyelidsTiredHeightNext) / 2);
      s.eyelidsAngryHeight = Math.floor((s.eyelidsAngryHeight + s.eyelidsAngryHeightNext) / 2);
      s.eyelidsHappyBottomOffset = Math.floor((s.eyelidsHappyBottomOffset + s.eyelidsHappyBottomOffsetNext) / 2);
      if (s.eyelidsTiredHeight > 0 || s.eyelidsAngryHeight > 0 || s.eyelidsHappyBottomOffset > 0) {
        ctx.save();
        ctx.globalCompositeOperation = 'destination-out';
        if (s.eyelidsTiredHeight > 0) {
          fillTriangle(ctx, s.eyeLx, s.eyeLy - 1, s.eyeLx + s.eyeWCurrent, s.eyeLy - 1, s.eyeLx, s.eyeLy + s.eyelidsTiredHeight - 1, BG);
          fillTriangle(ctx, s.eyeRx, s.eyeRy - 1, s.eyeRx + s.eyeWCurrent, s.eyeRy - 1, s.eyeRx + s.eyeWCurrent, s.eyeRy + s.eyelidsTiredHeight - 1, BG);
        }
        if (s.eyelidsAngryHeight > 0) {
          fillTriangle(ctx, s.eyeLx, s.eyeLy - 1, s.eyeLx + s.eyeWCurrent, s.eyeLy - 1, s.eyeLx + s.eyeWCurrent, s.eyeLy + s.eyelidsAngryHeight - 1, BG);
          fillTriangle(ctx, s.eyeRx, s.eyeRy - 1, s.eyeRx + s.eyeWCurrent, s.eyeRy - 1, s.eyeRx, s.eyeRy + s.eyelidsAngryHeight - 1, BG);
        }
        if (s.eyelidsHappyBottomOffset > 0) {
          fillRoundRect(ctx, s.eyeLx - 1, s.eyeLy + s.eyeHCurrentL - s.eyelidsHappyBottomOffset + 1, s.eyeWCurrent + 2, s.eyeHDefault, s.borderRCurrentL, BG);
          fillRoundRect(ctx, s.eyeRx - 1, s.eyeRy + s.eyeHCurrentR - s.eyelidsHappyBottomOffset + 1, s.eyeWCurrent + 2, s.eyeHDefault, s.borderRCurrentR, BG);
        }
        ctx.restore();
      }
    };
    let raf = 0;
    const loop = () => {
      tick(Date.now());
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [emotion, accent, active, motionBoost]);
  return <div className="w-full h-full flex items-center justify-center">
      <canvas ref={canvasRef} width={CONTENT_W} height={CONTENT_H} className="block" />
    </div>;
}
